import { useEffect, useState } from "react";
import { Typography, Tag, Spin } from "antd";
import { useProductData } from "../hooks/useProductData";
import { useNavigate } from "react-router-dom";

const { Title, Paragraph } = Typography;

export default function TourTabsDynamic({ slug, slides = [] }) {
  const { product, loading, error } = useProductData(slug);
  const [activeSub, setActiveSub] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false); // ✅ NEW
  const navigate = useNavigate();
  const subProducts = product?.sub_products || [];
  const [loadingRedirect, setLoadingRedirect] = useState(false);

  useEffect(() => {
    if (subProducts.length > 0 && !activeSub) {
      setActiveSub(subProducts[0].id);
    }
  }, [subProducts, activeSub]);

  if (loading) return <p className="text-center">Loading...</p>;
  if (error) return <p className="text-center text-red-500">{error}</p>;

  if (loadingRedirect) {
    return (
      <div className="flex justify-center items-center h-screen bg-white/70">
        <Spin size="large" tip="Loading product details..." />
      </div>
    );
  }

  return (
    <div className="container">
      {/* Title */}
      <div className="flex justify-center align-middle items-center mb-6 font-medium text-center">
        <div>
          <Title className="!font-medium !text-danger !text-2xl !mb-1" level={2}>
            <span className="text-primary"> Explore </span> Dubai Tours
          </Title>
          <Paragraph className="!mb-0">
            Culture, nature, thrills, and record-breaking experiences
          </Paragraph>
        </div>
      </div>

      {/* ✅ MOBILE BUTTON */}
      <div className="md:hidden mb-4">
        <button
          onClick={() => setShowSidebar(true)}
          className="px-4 py-2 bg-primary text-white rounded"
        >
          Categories
        </button>
      </div>

      {/* ✅ OVERLAY */}
      {showSidebar && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={() => setShowSidebar(false)}
        />
      )}

      <div className="flex flex-col md:flex-row gap-6 border-t border-b border-[#CCE1F9]">
        
        {/* LEFT: Subproduct Filter */}
        <div
          className={`
            fixed top-0 left-0 h-full w-3/4 bg-white z-50 p-4
            transform transition-transform duration-300
            md:static md:translate-x-0 md:w-1/4 md:block
            ${showSidebar ? "translate-x-0" : "-translate-x-full"}
          `}
        >
          {/* ✅ Mobile Header */}
          <div className="md:hidden flex justify-between items-center mb-4">
            <h3 className="font-semibold">Categories</h3>
            <button onClick={() => setShowSidebar(false)}>✕</button>
          </div>

          <Paragraph className="font-semibold text-lg mb-4">
            Categories
          </Paragraph>

          {subProducts.map((sub) => (
            <div
              key={sub.id}
              onClick={() => {
                setActiveSub(sub.id);
                setShowSidebar(false); // ✅ close on select
              }}
              className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition ${
                activeSub === sub.id
                  ? "bg-[#F8EBEB] bg-opacity-70"
                  : "hover:bg-gray-50"
              }`}
            >
              <img
                src={
                  sub.sub_product_image
                    ? `${process.env.REACT_APP_API_URL}${sub.sub_product_image}`
                    : "https://via.placeholder.com/100x80?text=No+Image"
                }
                alt={sub.title}
                className="w-24 h-20 object-cover rounded-lg"
              />

              <div>
                <p className="text-sm font-semibold">{sub.title}</p>
                <span className="text-xs text-danger flex items-center">
                  <span className="mr-2 text-xl drop-shadow-[0_1px_2px_rgba(0,0,0,0.25)]">
                    ★
                  </span>
                  4.9 (523)
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* RIGHT: Product Details */}
        <div className="w-full md:w-3/4 py-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {subProducts
              .filter((s) => s.id === activeSub)
              .flatMap((s) => s.product_details || [])
              .map((detail) => (
                <div
                  key={detail.id}
                  onClick={() => {
                    const subProduct = subProducts.find(
                      (s) => s.id === activeSub
                    );
                    const subSlug =
                      subProduct?.slug ||
                      subProduct?.title?.toLowerCase().replace(/\s+/g, "-") ||
                      "subtour";

                    navigate(`/${slug}/${detail.slug}`);
                  }}
                  className="cursor-pointer bg-white rounded-xl overflow-hidden relative shadow-custom hover:shadow-2xl"
                >
                  {/* Image */}
                  <div className="relative">
                    <div className="absolute overflow-hidden bg-black/20 w-full h-[100%]">
                      <div className="absolute bottom-0 left-0 right-0 p-4 text-left">
                        <div className="text-base font-semibold text-white">
                          INR {detail.final_price?.toLocaleString("en-IN")}
                        </div>
                        <div className="line-through font-normal text-white text-md">
                          INR {detail.old_price?.toLocaleString("en-IN")}
                        </div>
                        <div className="font-normal text-white text-md">
                          INR {detail.save_price?.toLocaleString("en-IN")}
                        </div>
                      </div>
                    </div>

                    <img
                      src={
                        `${process.env.REACT_APP_API_URL}${detail.images?.[0]?.url}` ||
                        "https://via.placeholder.com/300x200"
                      }
                      alt={detail.title}
                      className="w-full h-[170px] object-cover"
                    />

                    {/* Rating Badge */}
                    <div className={`bg-primary absolute bottom-[-25px] right-6 rounded-full shadow-lg text-white text-sm flex justify-center w-[80px] h-[80px] ${detail.badgeColor}`}>
                      <div className="text-center leading-tight mt-2">
                        <div className="text-3xl">★</div>
                        <div className="text-xs font-normal">4.9 (523)</div>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-5 pt-3">
                    <div className="mt-3 mb-2 text-muted text-[11px] font-medium">
                      Tour Type
                      <Tag className="bg-blue-100 text-black font-medium rounded border-0 text-[11px] ml-2">
                        {detail.tour_type}
                      </Tag>
                    </div>

                    <div className="font-semibold text-black text-md leading-snug">
                      {detail.title}
                    </div>

                    <div className="text-primary mt-2 font-medium">
                      9 days & 8 nights
                    </div>

                    <div className="text-[11px] text-muted mt-3 space-x-1 font-medium">
                      <span className="text-red-500 font-semibold">2D</span> Rome →
                      <span className="text-red-500 font-semibold">1D</span> Florence →
                      <span className="text-red-500 font-semibold">2D</span> Venice →
                      <span className="text-red-500 font-semibold">4D</span> Zurich
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}